// MapPage — the home of Fyrefly. ForeFlight-style: persistent top command
// bar + search, the map fills everything below, sheets slide in over it.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MapView, { recenter, pickFeaturesAt, pickWxCamAt, pickPirepAt, pickObstacleAt, pickRepeaterAt, pickTrafficAt, queryAirspaceAt, pickUserWaypointAt, pickDipSiteAt, pickIhogRingAt } from '../map/MapView';
import { PlateOverlayLayer } from '../map/PlateOverlayLayer';
import { HoldingsMapLayer } from '../map/HoldingsMapLayer';
import { PlanProceduresLayer } from '../map/PlanProceduresLayer';
import { GlideRingLayer } from '../map/GlideRingLayer';
import { CirclingAreaLayer, CirclingAreaLegend } from '../map/CirclingAreaLayer';
import { EmergencyGlideButton } from '../map/EmergencyGlideButton';
import { PlateOverlayControls } from '../map/PlateOverlayControls';
import UserWaypointDetail, { type UserWaypointSeed } from '../airport/UserWaypointDetail';
import TrafficDetail from '../airport/TrafficDetail';
import WxCamDetail, { type WxCamSeed } from '../airport/WxCamDetail';
import PirepDetail, { type PirepSeed } from '../airport/PirepDetail';
import DipSiteDetail, { type DipSiteSeed } from '../airport/DipSiteDetail';
import RubberBandPicker, { type RubberBandCandidate } from '../map/RubberBandPicker';
import LongPressPicker, { type LongPressHit, type AirspaceHit, type DirectToTarget } from '../map/LongPressPicker';
import WxHazardBar from '../map/WxHazardBar';
import NexradBar from '../map/NexradBar';
import SfcAnalysisBar from '../map/SfcAnalysisBar';
import SmokeBar from '../map/SmokeBar';
import TerrainAdvisorBar from '../map/TerrainAdvisorBar';
import AlertCenter from '../map/AlertCenter';
import AltitudeSlider from '../map/AltitudeSlider';
import { WindAltitudeControl } from '../components/WindAltitudeControl';
import { WindLegend } from '../components/WindLegend';
import { useAlertTriggers } from '../lib/alertTriggers';
import NavaidDetail, { type NavaidDetailData } from '../airport/NavaidDetail';
import AirspaceDetail, { type AirspaceDetailData, type AirspaceKind } from '../airport/AirspaceDetail';
import { fetchBbox, type BboxNavaid } from '../lib/mapBbox';
import { greatCircleNm } from '../lib/geo';
import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { trackAuditEvent } from '../lib/audit';
import DipSiteRecorder from '../components/DipSiteRecorder';
import { useDipSiteStore } from '../stores/dipSiteStore';
import { useGpsStore } from '../stores/gpsStore';
import { useTrackStore } from '../stores/trackStore';
import { usePlanStore } from '../stores/planStore';
import MapTopBar from '../map/MapTopBar';
import AeroKnobCluster from '../map/AeroKnobCluster';
import LayerSheet from './LayerSheet';
import PlanPanel from './PlanPanel';
import DetailPane from '../map/DetailPane';
import AirportDetail from '../airport/AirportDetail';
import PickList from '../map/PickList';
import Ruler from '../map/Ruler';
import { SearchHit } from '../lib/search';
import { Icon } from '../shell/Icon';
import { useIsDesktop } from '../shell/useBreakpoint';
import {
  useInstrumentContextValue,
  InstrumentContextProvider,
  InstrumentSelector,
} from '../map/instrumentBar';
import { INSTRUMENT_BY_ID } from '../lib/instruments';
import { useAirborneStore } from '../stores/airborneStore';
import { Car, Flame, Plane } from '../shell/Icon';
import { FireMissionLayer, type TfrPopupData, type IncidentPopupData } from '../map/FireMissionLayer';
import FireOpsPanel from '../map/FireOpsPanel';
import TailLookupSheet from './TailLookupSheet';
import TailTrackLayer from '../map/TailTrackLayer';
import type { FlightTrackResponse } from '../../api/flightaware/flight-track';
import { useFlightTrackPreviewStore } from '../stores/flightTrackPreviewStore';

export default function MapPage() {
  const [showLayers, setShowLayers] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [planClick, setPlanClick] = useState(false);
  /** When armed, the next map tap creates a single-leg flight plan
   *  from the current GPS fix (or fallback origin) to the tapped
   *  feature / point. Mirrors ForeFlight / Garmin Pilot 'Direct To'. */
  const [dctMode, setDctMode] = useState(false);
  const [detail, setDetail] = useState<SearchHit | null>(null);
  const [pickList, setPickList] = useState<{
    hits: SearchHit[];
    point: { x: number; y: number };
  } | null>(null);
  const [rubberBand, setRubberBand] = useState<{
    legIndex: number;
    point: { x: number; y: number };
    candidates: RubberBandCandidate[];
    loading: boolean;
  } | null>(null);
  const [longPress, setLongPress] = useState<{
    point: { x: number; y: number };
    /** Geo coordinate of the press — used by the picker for the
     *  COORDINATES row and as the lat/lon for "Direct to tapped
     *  point". */
    lngLat: [number, number];
    hits: LongPressHit[];
    loading: boolean;
  } | null>(null);
  const [navaidDetail, setNavaidDetail] = useState<NavaidDetailData | null>(null);
  const [airspaceDetail, setAirspaceDetail] = useState<AirspaceDetailData | null>(null);
  const [dipRecorder, setDipRecorder] = useState<{ lat: number; lon: number } | null>(null);
  const [wxCam, setWxCam] = useState<WxCamSeed | null>(null);
  const [pirep, setPirep] = useState<PirepSeed | null>(null);
  const [trafficIcao, setTrafficIcao] = useState<string | null>(null);
  const [userWptDetail, setUserWptDetail] = useState<UserWaypointSeed | null>(null);
  const [dipSiteDetail, setDipSiteDetail] = useState<DipSiteSeed | null>(null);
  const [showFireOps, setShowFireOps] = useState(false);
  // Tail-number lookup state
  const [tailLookupIdent, setTailLookupIdent] = useState<string | null>(null);
  const [activeFlightTrack, setActiveFlightTrack] = useState<FlightTrackResponse | null>(null);
  const [tfrPopup, setTfrPopup] = useState<TfrPopupData | null>(null);
  const [incidentPopup, setIncidentPopup] = useState<IncidentPopupData | null>(null);
  const navigate = useNavigate();
  // Desktop-only ruler toggle. The touch path is always live (two-finger
  // long-press); the FAB only controls click-and-drag mode for mouse
  // users — and the FAB only renders on desktop, since touch users
  // already have the gesture.
  const isDesktop = useIsDesktop();
  const [rulerDesktopActive, setRulerDesktopActive] = useState(false);
  // Pull current planned cruise altitude + ETD from the plan store so
  // the ruler can sample winds at the same level the user is going to fly.
  // Defaults are baked into Ruler if these are null.
  const planCruiseAlt = usePlanStore((s) => s.cruiseAlt);
  const planEtd = usePlanStore((s) => s.etd);

  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const toggleInstruments = useInstrumentPrefsStore((s) => s.toggleInstruments);

  // (Codex audit 2026-05-18, finding 1) MapPage used to subscribe to
  // `useGpsStore(s => s.fix)` but never read the value during render —
  // it only used `useGpsStore.getState().fix` inside event handlers.
  // That subscription caused MapPage (and every child: MapView, every
  // overlay layer, every sheet, picker, instrument) to re-render on
  // every GPS tick (~1 Hz, faster on Bluetooth ADS-B). The largest
  // single lag source in the app. Subscribing to `watching` alone is
  // enough — it changes at most twice per session.
  const gpsWatching = useGpsStore((s) => s.watching);
  const startGps = useGpsStore((s) => s.start);

  // Record state/controls now live in AeroKnobCluster; MapPage no
  // longer needs to reference them directly.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _trackRecording = useTrackStore((s) => s.recording);

  useEffect(() => {
    if (!gpsWatching) startGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consume a pending flight-track set by AircraftPage's Recent Flights tab.
  // AircraftPage stores the track here and navigates to /map; we pick it up
  // once on mount and render it exactly like TailLookupSheet does.
  const consumeFlightTrackPreview = useFlightTrackPreviewStore((s) => s.consume);
  useEffect(() => {
    const pending = consumeFlightTrackPreview();
    if (pending) setActiveFlightTrack(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consume any pending airport selection set by the Airports list
  // overlay. When the user taps an airport row in /airports, that page
  // pushes the pick into the ui store and navigates to /map; we pick
  // it up here, recenter the map, open the rich AirportDetail panel,
  // and clear the slot.
  const pendingAirport = useUiStore((s) => s.pendingAirport);
  useEffect(() => {
    if (!pendingAirport) return;
    const { ident, lat, lon, name } = pendingAirport;
    recenter(lon, lat, 11);
    setPickList(null);
    setDetail({ kind: 'airport', ident, lat, lon, name } as SearchHit);
    useUiStore.getState().setPendingAirport(null);
  }, [pendingAirport]);

  // Generic one-shot map jump — used by IncidentsPage "Show on map".
  // Recenters at the caller-chosen zoom, clears any open detail card
  // (so the map breathes once we land on the fire), and shows a brief
  // toast banner identifying what we're focused on.
  const pendingMapJump = useUiStore((s) => s.pendingMapJump);
  const [mapJumpBanner, setMapJumpBanner] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingMapJump) return;
    const { lat, lon, zoom, label, irwinId } = pendingMapJump;
    // Force the NIFC fires layer ON when we're jumping to a fire so
    // the perimeter polygon is visible. (irwinId being set is our
    // signal that the jump source is a fire incident.)
    if (irwinId) {
      useLayerVisibilityStore.getState().toggleLayer('fires', true);
    }
    recenter(lon, lat, zoom);
    setPickList(null);
    setDetail(null);
    setNavaidDetail(null);
    setAirspaceDetail(null);
    setPirep(null);
    setWxCam(null);
    setDipSiteDetail(null);
    if (label) {
      setMapJumpBanner(label);
      window.setTimeout(() => setMapJumpBanner(null), 3000);
    }
    useUiStore.getState().setPendingMapJump(null);
  }, [pendingMapJump]);

  const onMapClick = (
    lngLat: [number, number],
    point: { x: number; y: number },
  ) => {
    // Tap-to-add waypoint mode wins when armed; nothing else fires.
    if (planClick) {
      const ident = `USR${usePlanStore.getState().route.length + 1}`;
      usePlanStore.getState().add({ ident, lat: lngLat[1], lon: lngLat[0], kind: 'user' });
      // One-shot: disarm after a successful drop so the user doesn't
      // keep dropping waypoints accidentally.
      setPlanClick(false);
      return;
    }

    // Direct-To mode: next tap commits a one-leg plan from GPS (or
    // a sensible fallback) to the tapped feature / point.
    if (dctMode) {
      let target: { ident: string; lat: number; lon: number; kind: 'airport' | 'fix' | 'navaid' | 'user' } | null = null;
      const hits = pickFeaturesAt(point, 18);
      if (hits.length > 0) {
        const h = hits[0];
        target = { ident: h.ident, lat: h.lat, lon: h.lon, kind: h.kind };
      } else {
        target = { ident: 'DCT', lat: lngLat[1], lon: lngLat[0], kind: 'user' };
      }
      const gps = useGpsStore.getState().fix;
      const existing = usePlanStore.getState().route;
      let start: { ident: string; lat: number; lon: number; kind: 'airport' | 'fix' | 'navaid' | 'user' } | null = null;
      if (gps) {
        start = { ident: 'HERE', lat: gps.lat, lon: gps.lon, kind: 'user' };
      } else if (existing.length > 0) {
        // No GPS: use the start of the existing route so we don't
        // silently lose context. UI hint surfaced below.
        const s = existing[0];
        start = { ident: s.ident, lat: s.lat, lon: s.lon, kind: (s.kind ?? 'user') as any };
      }
      if (!start) {
        alert('Direct To needs a starting point.\nEnable GPS or add a route start first.');
        setDctMode(false);
        return;
      }
      usePlanStore.getState().directTo(start, target!);
      setDctMode(false);
      // Surface the new plan immediately so the pilot sees what was
      // built. Don't auto-open the panel — just a quick visual
      // confirmation via the plan-line on the map.
      return;
    }

    // User waypoints — check before generic features since they're user-owned
    // and should take priority over other map icons at the same location.
    const wptHit = pickUserWaypointAt(point, 22);
    if (wptHit) {
      setDetail(null);
      setPickList(null);
      setNavaidDetail(null);
      setAirspaceDetail(null);
      setWxCam(null);
      setPirep(null);
      setDipSiteDetail(null);
      setUserWptDetail(wptHit);
      return;
    }

    // Dip sites — colored circles for water sources. Tap to see
    // ownership / reimbursement / recent uses + a re-lookup button.
    // Tap radius slightly larger than the icon (24px) since the
    // circle marker is small. Checked after user waypoints so a
    // user-owned marker stacked on top still wins.
    const dipHit = pickDipSiteAt(point, 24);
    if (dipHit && typeof dipHit.dipSiteId === 'string') {
      setDetail(null);
      setPickList(null);
      setNavaidDetail(null);
      setAirspaceDetail(null);
      setWxCam(null);
      setPirep(null);
      setUserWptDetail(null);
      setDipSiteDetail({
        dipSiteId:           String(dipHit.dipSiteId),
        name:                String(dipHit.name ?? 'Dip site'),
        ownershipClass:      String(dipHit.ownershipClass ?? 'unknown'),
        managingAgency:      (dipHit.managingAgency as string | null) ?? null,
        ownerName:           (dipHit.ownerName as string | null) ?? null,
        reimbursementStatus: String(dipHit.reimbursementStatus ?? 'unknown'),
        sourceType:          String(dipHit.sourceType ?? 'pond'),
        lat:                 dipHit.lat,
        lon:                 dipHit.lon,
      });
      return;
    }

    // Weather-cam icons get checked first — they have their own detail
    // surface (camera image grid) and aren't part of the SearchHit
    // pipeline. A wxcam tap dismisses any other open detail card.
    const wxcamHit = pickWxCamAt(point, 18);
    if (wxcamHit) {
      setDetail(null);
      setPickList(null);
      setNavaidDetail(null);
      setAirspaceDetail(null);
      setPirep(null);
      setWxCam(wxcamHit);
      return;
    }

    // PIREPs same idea — their own slide-over with raw + plain text.
    const pirepHit = pickPirepAt(point, 18);
    if (pirepHit) {
      setDetail(null);
      setPickList(null);
      setNavaidDetail(null);
      setAirspaceDetail(null);
      setWxCam(null);
      setPirep({ properties: pirepHit });
      return;
    }

    // Tall obstacles route through the AirspaceDetail surface
    // (cheaper than a fourth slide-over; the 'what is this thing?'
    // UX is identical). Tap-radius slightly wider than the icon's
    // bounding box so a fingertip near the mast still picks it up.
    const obstacleHit = pickObstacleAt(point, 18);
    if (obstacleHit) {
      setDetail(null);
      setPickList(null);
      setNavaidDetail(null);
      setWxCam(null);
      setPirep(null);
      setAirspaceDetail({ kind: 'obstacle', properties: obstacleHit });
      return;
    }

    // NIFC repeater sites — same AirspaceDetail surface, kind
    // 'repeater'. Show the primary channel (RX/TX/tone) tied to this
    // site plus the full list of channels the parent dispatch center
    // serves. Tap radius matches obstacle/wxcam (18 px).
    const repeaterHit = pickRepeaterAt(point, 18);
    if (repeaterHit) {
      setDetail(null);
      setPickList(null);
      setNavaidDetail(null);
      setWxCam(null);
      setPirep(null);
      setAirspaceDetail({ kind: 'repeater', properties: repeaterHit });
      return;
    }

    // ADS-B traffic targets — their own slide-over with live altitude,
    // groundspeed, vert rate, distance/bearing from ownship, source
    // badge. Tap radius matches wxcam/obstacle (18 px).
    const trafficHit = pickTrafficAt(point, 18);
    if (trafficHit) {
      const hex = typeof trafficHit.icaoHex === 'string' ? trafficHit.icaoHex : null;
      if (hex) {
        setDetail(null);
        setPickList(null);
        setNavaidDetail(null);
        setAirspaceDetail(null);
        setWxCam(null);
        setPirep(null);
        setDipSiteDetail(null);
        setTrafficIcao(hex);
        return;
      }
    }

    // Default tap behavior: feature pick. ~18px radius is roughly a
    // fingertip target on iPad without sweeping in too many neighbors.
    const hits = pickFeaturesAt(point, 18);
    if (hits.length === 0) {
      // Empty-space tap, but check IHOG proximity rings first — a
      // tap inside an active-incident ring navigates to that incident's
      // detail page (Wiley feedback: "clicking a ring should open the
      // incident"). Checked AFTER pickFeaturesAt(==0) so airport /
      // navaid / fix taps inside the ring still win.
      const ringHit = pickIhogRingAt(point);
      if (ringHit) {
        setDetail(null);
        setPickList(null);
        setNavaidDetail(null);
        setAirspaceDetail(null);
        setWxCam(null);
        setPirep(null);
        setDipSiteDetail(null);
        navigate(`/incidents/${ringHit.incidentId}`);
        return;
      }
      // Nothing under the fingertip in the airport/fix/navaid layers.
      // Before dismissing, check whether an airspace polygon (TFR /
      // SUA / AIRMET / SIGMET) covers the tap point so the user can
      // single-tap a TFR to read its info — not just long-press.
      // (Wiley feedback 2026-05-09: "TFRs are graphically displayed
      //  but you can't click on them and get any of the information.")
      const airspaceRaw = queryAirspaceAt(point);
      if (airspaceRaw.length === 1) {
        setPickList(null);
        setDetail(null);
        setAirspaceDetail({ kind: airspaceRaw[0].kind, properties: airspaceRaw[0].properties });
        emitTfrViewedIfApplicable(airspaceRaw[0].kind, airspaceRaw[0].properties);
        return;
      }
      if (airspaceRaw.length > 1) {
        // Multiple airspace polygons stacked at this point — surface
        // them through the same long-press picker UI which already
        // knows how to render an airspace-only hit list.
        const airspaceHits: AirspaceHit[] = airspaceRaw.map((a) => ({
          kind: 'airspace',
          airspaceKind: a.kind,
          title: airspaceTitle(a.kind, a.properties),
          subtitle: airspaceSubtitle(a.kind, a.properties),
          properties: a.properties,
        }));
        setDetail(null);
        setPickList(null);
        setLongPress({ point, lngLat, hits: airspaceHits, loading: false });
        return;
      }
      // Empty space: dismiss any open ad-hoc surfaces so the map
      // breathes. Keep PlanPanel/LayerSheet alone — those are
      // user-driven UI, not tap-driven.
      setDetail(null);
      setPickList(null);
      return;
    }
    if (hits.length === 1) {
      setPickList(null);
      setDetail(hits[0]);
      return;
    }
    // Multiple hits: show a chooser anchored near the tap.
    setDetail(null);
    setPickList({ hits, point });
  };

  const followOwnship = useUiStore((s) => s.followOwnship);
  const setFollowOwnship = useUiStore((s) => s.setFollowOwnship);

  const onCenterOnGps = () => {
    const f = useGpsStore.getState().fix;
    if (f) {
      recenter(f.lon, f.lat);
      setFollowOwnship(true);
    } else {
      // No fix: fall back to first route waypoint or current behavior
      const route = usePlanStore.getState().route;
      if (route.length > 0) recenter(route[0]!.lon, route[0]!.lat);
    }
  };

  const onSearchHit = (hit: SearchHit) => {
    // Fly to and zoom in. Open the detail pane.
    recenter(hit.lon, hit.lat, hit.kind === 'airport' ? 11 : 9);
    setPickList(null);
    setDetail(hit);
  };

  /**
   * The user finished dragging a flight-plan leg to a new map point.
   * Build the candidate list:
   *   1. The raw GPS coordinate (always available)
   *   2. Up to 4 nearest airports within ~25 NM
   *   3. Up to 4 nearest fixes within ~15 NM
   *   4. Up to 3 nearest navaids within ~25 NM
   *
   * Each is sorted by distance from the drop point. Tapping a row
   * inserts that candidate into the route at legIndex+1.
   */
  const openRubberBandPicker = async (
    legIndex: number,
    dropLngLat: [number, number],
    screenPoint: { x: number; y: number },
  ) => {
    setPickList(null);
    setDetail(null);
    const [lon, lat] = dropLngLat;
    // GPS-pin candidate stays as a fallback but is no longer pinned
    // to the top — boss reported it pushed the actual nearby airport
    // below it even when the line was dropped right on top of that
    // airport. We now sort all candidates by distance from the drop
    // point so whatever you dropped near is first.
    const userIdent = `USR${usePlanStore.getState().route.length + 1}`;
    const gpsRow: RubberBandCandidate = {
      ident: userIdent,
      lat, lon,
      kind: 'user',
      name: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
      rowKind: 'gps',
      distNm: 0,
    };
    setRubberBand({
      legIndex,
      point: screenPoint,
      candidates: [gpsRow],
      loading: true,
    });

    // Look up nearby features in parallel. The bbox endpoint takes a
    // viewport rectangle so we ask for a generous 0.5° box around the
    // drop point and then sort by exact great-circle distance.
    const PAD_DEG = 0.5;
    const bboxArgs = {
      west:  lon - PAD_DEG,
      east:  lon + PAD_DEG,
      south: lat - PAD_DEG,
      north: lat + PAD_DEG,
      zoom:  10, // gates the server's filtering; 10 returns everything
    };
    let airports: Array<RubberBandCandidate> = [];
    let fixes:    Array<RubberBandCandidate> = [];
    let navaids:  Array<RubberBandCandidate> = [];
    try {
      const [aps, fxs, navs] = await Promise.all([
        fetchBbox({ ...bboxArgs, layer: 'airports', limit: 50 }).catch(() => []),
        fetchBbox({ ...bboxArgs, layer: 'fixes',    limit: 50 }).catch(() => []),
        fetchBbox({ ...bboxArgs, layer: 'navaids',  limit: 30 }).catch(() => []),
      ]);
      airports = aps
        .map((a) => ({
          ident: a.ident, lat: a.lat, lon: a.lon,
          kind: 'airport' as const,
          name: a.name,
          distNm: Math.round(greatCircleNm(lat, lon, a.lat, a.lon)),
          rowKind: 'airport' as const,
        }))
        .filter((c) => (c.distNm ?? 0) <= 25)
        .sort((a, b) => (a.distNm ?? 0) - (b.distNm ?? 0))
        .slice(0, 4);
      fixes = fxs
        .map((f) => ({
          ident: f.ident, lat: f.lat, lon: f.lon,
          kind: 'fix' as const,
          name: f.cat || '',
          distNm: Math.round(greatCircleNm(lat, lon, f.lat, f.lon)),
          rowKind: 'fix' as const,
        }))
        .filter((c) => (c.distNm ?? 0) <= 15)
        .sort((a, b) => (a.distNm ?? 0) - (b.distNm ?? 0))
        .slice(0, 4);
      navaids = navs
        .map((n) => {
          const t = (n.type || '').toUpperCase();
          const planKind: RubberBandCandidate['kind'] =
            t.includes('NDB') ? 'ndb' : 'vor';
          return {
            ident: n.ident, lat: n.lat, lon: n.lon,
            kind: planKind,
            name: `${n.name} · ${n.type}`,
            distNm: Math.round(greatCircleNm(lat, lon, n.lat, n.lon)),
            rowKind: 'navaid' as const,
          };
        })
        .filter((c) => (c.distNm ?? 0) <= 25)
        .sort((a, b) => (a.distNm ?? 0) - (b.distNm ?? 0))
        .slice(0, 3);
    } catch (e) {
      // Best-effort — if the bbox calls fail, the user still gets
      // the GPS-pin row. Log so upstream outages are visible.
      console.warn('[map] rubber-band bbox fetch failed:', e);
    }
    // Sort every real candidate by distance from the drop point, then
    // append the GPS-pin row last (it's a manual fallback, not a
    // 'thing you dropped on'). If a real airport sits within ~1nm of
    // the drop we drop the GPS pin entirely — the airport IS the
    // intent. (Bug fix 2026-05-12: boss dropped on top of an airport
    // and got the GPS pin first, which felt wrong.)
    const realCandidates: RubberBandCandidate[] = [
      ...airports, ...fixes, ...navaids,
    ].sort((a, b) => (a.distNm ?? Infinity) - (b.distNm ?? Infinity));
    const closestDist = realCandidates[0]?.distNm ?? Infinity;
    const includeGps = closestDist > 1; // nm
    const candidates: RubberBandCandidate[] = includeGps
      ? [...realCandidates, gpsRow]
      : realCandidates;
    setRubberBand((rb) => rb ? { ...rb, candidates, loading: false } : rb);
  };

  const pickRubberBandCandidate = (c: RubberBandCandidate) => {
    if (!rubberBand) return;
    usePlanStore.getState().insertAt(rubberBand.legIndex + 1, {
      ident: c.ident, lat: c.lat, lon: c.lon, kind: c.kind ?? 'user',
    });
    setRubberBand(null);
  };

  /**
   * The user long-pressed somewhere on the map. Build a sectioned
   * candidate list of nearby airports / navaids / fixes plus any
   * airspace polygons that contain the press point. ForeFlight's
   * "long-press info" menu.
   *
   * Airspace is queried first (synchronous, comes from already-loaded
   * GeoJSON layers) so the user sees something fast even before the
   * bbox round-trips return.
   */
  const openLongPressPicker = async (
    lngLat: [number, number],
    screenPoint: { x: number; y: number },
  ) => {
    setPickList(null);
    setRubberBand(null);
    setDetail(null);
    setNavaidDetail(null);
    setAirspaceDetail(null);

    const [lon, lat] = lngLat;
    const airspaceRaw = queryAirspaceAt(screenPoint);
    const airspaceHits: AirspaceHit[] = airspaceRaw.map((a) => ({
      kind: 'airspace',
      airspaceKind: a.kind,
      title: airspaceTitle(a.kind, a.properties),
      subtitle: airspaceSubtitle(a.kind, a.properties),
      properties: a.properties,
    }));

    setLongPress({
      point: screenPoint,
      lngLat,
      hits: airspaceHits, // shows airspace immediately while we wait on bbox
      loading: true,
    });

    const PAD_DEG = 0.5;
    const bboxArgs = {
      west:  lon - PAD_DEG,
      east:  lon + PAD_DEG,
      south: lat - PAD_DEG,
      north: lat + PAD_DEG,
      zoom:  10,
    };
    let airports: SearchHit[] = [];
    let navaids: SearchHit[] = [];
    let fixes: SearchHit[] = [];
    try {
      const [aps, navs, fxs] = await Promise.all([
        fetchBbox({ ...bboxArgs, layer: 'airports', limit: 50 }).catch(() => []),
        fetchBbox({ ...bboxArgs, layer: 'navaids', limit: 30 }).catch(() => []),
        fetchBbox({ ...bboxArgs, layer: 'fixes', limit: 50 }).catch(() => []),
      ]);
      airports = aps
        .map((a) => ({
          kind: 'airport' as const,
          ident: a.ident, name: a.name,
          lat: a.lat, lon: a.lon,
          sub: `${Math.round(greatCircleNm(lat, lon, a.lat, a.lon))} nm`,
        }))
        .sort((a, b) => greatCircleNm(lat, lon, a.lat, a.lon) - greatCircleNm(lat, lon, b.lat, b.lon))
        .filter((c) => greatCircleNm(lat, lon, c.lat, c.lon) <= 25)
        .slice(0, 4);
      navaids = navs
        .map((n: BboxNavaid) => ({
          kind: 'navaid' as const,
          ident: n.ident, name: n.name,
          lat: n.lat, lon: n.lon,
          sub: `${n.type} · ${Math.round(greatCircleNm(lat, lon, n.lat, n.lon))} nm`,
          badge: n.type,
        }))
        .sort((a, b) => greatCircleNm(lat, lon, a.lat, a.lon) - greatCircleNm(lat, lon, b.lat, b.lon))
        .filter((c) => greatCircleNm(lat, lon, c.lat, c.lon) <= 25)
        .slice(0, 3);
      fixes = fxs
        .map((f) => ({
          kind: 'fix' as const,
          ident: f.ident, name: f.ident,
          lat: f.lat, lon: f.lon,
          sub: `${Math.round(greatCircleNm(lat, lon, f.lat, f.lon))} nm`,
        }))
        .sort((a, b) => greatCircleNm(lat, lon, a.lat, a.lon) - greatCircleNm(lat, lon, b.lat, b.lon))
        .filter((c) => greatCircleNm(lat, lon, c.lat, c.lon) <= 15)
        .slice(0, 4);
    } catch (e) {
      // best-effort — even an empty list shows airspace if any.
      // Log so upstream outages are visible in Vercel logs.
      console.warn('[map] long-press bbox fetch failed:', e);
    }

    const allHits: LongPressHit[] = [...airports, ...navaids, ...fixes, ...airspaceHits];
    setLongPress((lp) => lp ? { ...lp, hits: allHits, loading: false } : lp);
  };

  const pickLongPressHit = async (h: LongPressHit) => {
    setLongPress(null);
    if (h.kind === 'airport') {
      setDetail(h);
      return;
    }
    if (h.kind === 'navaid') {
      // Hydrate via bbox in a small box around the navaid so we get
      // the truthy NASR fields (magVar, elevFt, city, state).
      const PAD = 0.05;
      try {
        const navs = await fetchBbox({
          west: h.lon - PAD, east: h.lon + PAD,
          south: h.lat - PAD, north: h.lat + PAD,
          layer: 'navaids', limit: 20, zoom: 10,
        });
        const m = navs.find((n) => n.ident === h.ident);
        setNavaidDetail({
          ident: h.ident, name: h.name, type: m?.type || h.badge || '',
          lat: h.lat, lon: h.lon,
          freq: m?.freq, magVar: m?.magVar ?? null, magVarYear: m?.magVarYear ?? null,
          elevFt: m?.elevFt ?? null, city: m?.city, state: m?.state,
        });
      } catch (e) {
        console.warn('[map] navaid hydration fetch failed for', h.ident, ':', e);
        setNavaidDetail({
          ident: h.ident, name: h.name, type: h.badge || '', lat: h.lat, lon: h.lon,
        });
      }
      return;
    }
    if (h.kind === 'fix') {
      setDetail(h);
      return;
    }
    if (h.kind === 'airspace') {
      setAirspaceDetail({ kind: h.airspaceKind, properties: h.properties });
      emitTfrViewedIfApplicable(h.airspaceKind, h.properties);
    }
  };

  /**
   * ➜ Direct-To from the long-press picker. Builds a single-leg
   * plan from the current GPS fix (or the start of the existing
   * route as a fallback) to whichever target row the pilot tapped
   * — a nearby airport / navaid / fix, or the raw tap coordinate.
   *
   * Mirrors the existing top-bar Direct-To behavior in `onMapClick`
   * so both entry points share identical semantics.
   */
  const directToFromLongPress = (t: DirectToTarget) => {
    let target: { ident: string; lat: number; lon: number; kind: 'airport' | 'fix' | 'navaid' | 'user' };
    if (t.kind === 'coord') {
      const ident = `DCT${usePlanStore.getState().route.length + 1}`;
      target = { ident, lat: t.lat, lon: t.lon, kind: 'user' };
    } else {
      // SearchHit. PlanPointKind narrows to the four EFB-relevant
      // kinds; SearchHit.kind is already one of them.
      target = { ident: t.ident, lat: t.lat, lon: t.lon, kind: t.kind };
    }

    const gps = useGpsStore.getState().fix;
    const existing = usePlanStore.getState().route;
    let start: { ident: string; lat: number; lon: number; kind: 'airport' | 'fix' | 'navaid' | 'user' } | null = null;
    if (gps) {
      start = { ident: 'HERE', lat: gps.lat, lon: gps.lon, kind: 'user' };
    } else if (existing.length > 0) {
      const s = existing[0];
      start = { ident: s.ident, lat: s.lat, lon: s.lon, kind: (s.kind ?? 'user') as any };
    }
    if (!start) {
      alert('Direct To needs a starting point.\nEnable GPS or add a route start first.');
      return;
    }
    usePlanStore.getState().directTo(start, target);
    // Close the picker so the new plan-line is visible immediately.
    setLongPress(null);
    // Also clear any other ad-hoc surfaces — we just rewrote the
    // active route, the pilot wants to see the map.
    setDetail(null);
    setPickList(null);
    setNavaidDetail(null);
    setAirspaceDetail(null);
  };

  // Mutually-exclusive sheet logic: opening one closes the others so
  // they don't pile on a small screen. Closing the Plan panel also
  // disarms tap-to-add so a closed panel can't leave the user in a
  // hidden mode.
  const openLayers = () => {
    setShowLayers((v) => !v);
    setShowPlan(false);
    setDetail(null);
    setPickList(null);
    setRubberBand(null);
    setLongPress(null);
    setNavaidDetail(null);
    setAirspaceDetail(null);
    setPlanClick(false);
  };
  const openPlan = () => {
    setShowPlan((v) => {
      if (v) setPlanClick(false); // closing
      return !v;
    });
    setShowLayers(false);
    setDetail(null);
    setPickList(null);
    setRubberBand(null);
    setLongPress(null);
    setNavaidDetail(null);
    setAirspaceDetail(null);
  };

  return (
    // (Claude audit 2026-05-18, finding 1) Wrap everything in the
    // instrument-context provider so DataStrip + AlertTriggerWatcher +
    // AltitudeSliderHost share ONE useInstrumentContext() under the
    // hood instead of each mounting their own copy. Eliminates triple
    // network fetches per grid crossing + triple re-renders per GPS
    // tick.
    <InstrumentContextProvider>
    <div className="absolute inset-0">
      <MapView
        onMapClick={onMapClick}
        onPlanLegDrop={(legIndex, dropLngLat, screenPoint) =>
          openRubberBandPicker(legIndex, dropLngLat, screenPoint)
        }
        onLongPress={(lngLat, screenPoint) =>
          openLongPressPicker(lngLat, screenPoint)
        }
      />
      {/* Approach plate overlay — no DOM, pure MapLibre side-effect */}
      <PlateOverlayLayer />
      {/* Controls widget: demo launch + opacity slider */}
      <PlateOverlayControls />
      {/* Plan-derived procedure overlays (holds + PTs + entries) — visible
          whenever a SID/STAR/IAP is on the plan, even without the plate raster */}
      <PlanProceduresLayer />
      {/* Published holding patterns from NASR HPF.txt — racetrack overlay */}
      <HoldingsMapLayer />
      {/* Glide-ring — terrain-clipped emergency glide ring */}
      <GlideRingLayer />
      {/* Circling-area Phase C — TERPS-sized stadium polygon for the
         loaded IAP, sector restrictions hatched out. Advisory only. */}
      <CirclingAreaLayer />
      {/* Fire Mission Layer — TFRs + active fire incident dots.
          Default OFF; toggled via the Fire Ops panel (🔥 FAB). */}
      <FireMissionLayer
        onTfrClick={(data) => {
          setTfrPopup(data);
          setIncidentPopup(null);
        }}
        onIncidentClick={(data) => {
          setIncidentPopup(data);
          setTfrPopup(null);
        }}
      />
      {/* Tail-track layer — renders historical flight track when a tail lookup is active */}
      <TailTrackLayer track={activeFlightTrack} />
      {/* Emergency Glide floating button (bottom-right) */}
      <EmergencyGlideButton />

      <MapTopBar
        showLayers={showLayers}
        onToggleLayers={openLayers}
        showPlan={showPlan}
        onTogglePlan={openPlan}
        showInstruments={showInstruments}
        onToggleInstruments={() => toggleInstruments()}
        onCenterOnGps={onCenterOnGps}
        followOwnship={followOwnship}
        onSearchHit={onSearchHit}
        onTailLookup={(ident) => {
          setTailLookupIdent(ident);
          setActiveFlightTrack(null);
        }}
        dctMode={dctMode}
        onToggleDct={() => {
          // Arming Direct-To cancels any in-progress tap-to-add.
          if (!dctMode) setPlanClick(false);
          setDctMode((v) => !v);
        }}
      />
      {dctMode && (
        <div className="absolute z-30 left-1/2 -translate-x-1/2 top-[calc(var(--safe-top,0px)+5.5rem)] pointer-events-none">
          <div className="px-3 py-1.5 rounded-full bg-ember-600/95 border border-ember-400 text-[12px] text-white shadow-bar">
            Tap a destination — builds a Direct-To from your position
          </div>
        </div>
      )}

      {/* Circling-area legend chip — mandatory HUD when the layer is
         rendering. Top-center under the search bar (same anchor as the
         Direct-To banner) so it stays out of the way of the map. */}
      <div className="absolute z-20 left-1/2 -translate-x-1/2 top-[calc(var(--safe-top,0px)+5.5rem)] pointer-events-none">
        <CirclingAreaLegend />
      </div>

      {/* Right rail: ruler (desktop) + Fire Ops toggle. Record button moved
         to the left-side AeroKnobCluster so it lives next to the aero-layer
         controls. Fire Ops FAB is visible on all screen sizes. */}
      <div className="absolute z-20 right-3 bottom-44 flex flex-col gap-2 items-end">
        {isDesktop && (
          <button
            onClick={() => setRulerDesktopActive((v) => !v)}
            className={[
              'border rounded-lg h-11 w-11 flex items-center justify-center backdrop-blur',
              rulerDesktopActive
                ? 'bg-cyan-600/90 border-cyan-500 text-white'
                : 'bg-cockpit-800/90 border-cockpit-600',
            ].join(' ')}
            aria-label={rulerDesktopActive ? 'Exit ruler mode' : 'Measure distance'}
            title={rulerDesktopActive ? 'Exit ruler mode' : 'Measure distance — click-drag'}
          >
            <Icon name="ruler" />
          </button>
        )}
        {/* Fire Ops panel toggle — opens the Fire Ops bottom sheet.
            Uses ember/orange colors so it reads as 'fire' at a glance. */}
        <button
          onClick={() => setShowFireOps((v) => !v)}
          className={[
            'border rounded-lg h-11 w-11 flex items-center justify-center backdrop-blur',
            showFireOps
              ? 'bg-ember-600/90 border-ember-500 text-white'
              : 'bg-cockpit-800/90 border-cockpit-600 text-cockpit-200',
          ].join(' ')}
          aria-label="Fire Ops layers"
          title="Fire Ops — toggle active fires + TFRs"
        >
          <Flame size={18} aria-hidden />
        </button>
      </div>

      {/* Left-side knob cluster: Record + Aero layer toggle. */}
      <AeroKnobCluster />

      {/* Ruler (distance + wind-corrected ETE/fuel). Touch path is
          always live (two-finger long-press); desktop FAB toggles
          click-and-drag mode for mouse users. */}
      <Ruler
        desktopActive={rulerDesktopActive}
        cruiseAlt={planCruiseAlt}
        time={planEtd}
        onDesktopExit={() => setRulerDesktopActive(false)}
      />

      {/* AIRMET / SIGMET / CWA hazard bar (visible when wxhazards layer
          is on; sits above the instrument bar). */}
      <WxHazardBar />

      {/* NEXRAD radar time scrubber (-60m to NOW), only shows when the
          NEXRAD layer is on. Stacks above the GFA bar when both are
          active. */}
      <NexradBar />

      {/* Surface Analysis time scrubber (now → +7 days WPC PMSL
          forecast), only shows when the Surface Analysis layer is on. */}
      <SfcAnalysisBar />

      {/* HRRR-Smoke time scrubber (now → +18h forecast), only shows
          when the HRRR-Smoke layer is on. Stacks above SfcAnalysis. */}
      <SmokeBar />

      {/* Terrain Advisor color-key + settings gear, visible whenever
          the terrain layer is on. Stacks above the other bars. */}
      <TerrainAdvisorBar />

      {/* Floating toast-stack for terrain / runway / destination alerts. */}
      <AlertCenter />

      {/* One-shot "focused on X" banner — fires when the user hits
          "Show on map" on a WFIGS incident search row (or any other
          map-jump source). Auto-dismisses after 3 sec. */}
      {mapJumpBanner && (
        <div className="absolute z-30 top-16 left-1/2 -translate-x-1/2 bg-cockpit-800/95 backdrop-blur border border-ember-500 rounded-full px-4 py-1.5 shadow-rail text-sm text-cockpit-100">
          <span className="text-[10px] tracking-widest text-ember-300 mr-2">FOCUS</span>
          {mapJumpBanner}
        </div>
      )}

      {/* Right-hand vertical altitude slider — shared reference for
          altitude-aware layers (terrain advisor, future GFA altitude
          band filter). Visible whenever any such layer is on. */}
      <AltitudeSliderHost />

      {/* Wind-particles layer controls (altitude + forecast scrubber).
          Self-gated by layers.wind — renders nothing when wind is off. */}
      <WindAltitudeControl />
      <WindLegend />

      {/* Trigger watcher — binds GPS + plan + terrain elevation to
          alertStore pushes. Renders nothing. */}
      <AlertTriggerWatcher />

      {/* Bottom data strip (instruments) */}
      {showInstruments && <DataStrip />}

      {/* Sheets / overlays */}
      {showLayers && <LayerSheet onClose={() => setShowLayers(false)} />}
      {showPlan && (
        <PlanPanel
          onClose={() => { setShowPlan(false); setPlanClick(false); }}
          planClick={planClick}
          setPlanClick={setPlanClick}
        />
      )}
      {/* Search hits AND map taps open one of two detail surfaces:
          - Airports get the rich AirportDetail (FF-style bottom sheet on
            mobile, right-side pane on tablet/desktop) with full tabs.
          - Navaids and fixes use the lightweight DetailPane card. */}
      {detail && detail.kind === 'airport' && (
        <AirportDetail
          ident={detail.ident}
          initialLat={detail.lat}
          initialLon={detail.lon}
          initialName={detail.name}
          onClose={() => setDetail(null)}
        />
      )}
      {detail && detail.kind !== 'airport' && (
        <DetailPane hit={detail} onClose={() => setDetail(null)} />
      )}

      {/* Disambiguation sheet for taps that landed on multiple features
          at once. Tapping a row drills into the normal detail UI. */}
      {pickList && (
        <PickList
          hits={pickList.hits}
          point={pickList.point}
          onPick={(h) => {
            setPickList(null);
            setDetail(h);
          }}
          onClose={() => setPickList(null)}
        />
      )}

      {/* Rubber-band candidate sheet — appears when the user finishes
          dragging a flight-plan leg to a new map location. Lists the
          drop GPS point and nearby airports/fixes/navaids; tap a row
          to insert it into the route. */}
      {rubberBand && (
        <RubberBandPicker
          candidates={rubberBand.candidates}
          point={rubberBand.point}
          loading={rubberBand.loading}
          onPick={pickRubberBandCandidate}
          onClose={() => setRubberBand(null)}
        />
      )}

      {/* Long-press picker — sectioned list (airports / navaids /
          fixes / airspace) at the long-press point. Tapping a row
          drills into the matching detail card. */}
      {longPress && (
        <LongPressPicker
          hits={longPress.hits}
          point={longPress.point}
          lngLat={longPress.lngLat}
          loading={longPress.loading}
          onPick={pickLongPressHit}
          onDirectTo={directToFromLongPress}
          onRecordDip={(ll) => setDipRecorder(ll)}
          onClose={() => setLongPress(null)}
        />
      )}

      {/* Dip-site recorder modal — opens from the long-press picker. */}
      {dipRecorder && (
        <DipSiteRecorder
          lat={dipRecorder.lat}
          lon={dipRecorder.lon}
          onClose={() => setDipRecorder(null)}
          onSaved={() => { void useDipSiteStore.getState().refresh(); }}
        />
      )}

      {/* Navaid detail card — reachable via the long-press picker. */}
      {navaidDetail && (
        <NavaidDetail
          navaid={navaidDetail}
          onClose={() => setNavaidDetail(null)}
        />
      )}

      {/* Airspace detail card — reachable via the long-press picker. */}
      {airspaceDetail && (
        <AirspaceDetail
          airspace={airspaceDetail}
          onClose={() => setAirspaceDetail(null)}
        />
      )}
      {/* FAA WeatherCams detail card — reachable by tapping a wxcam icon. */}
      {wxCam && (
        <WxCamDetail
          seed={wxCam}
          onClose={() => setWxCam(null)}
        />
      )}
      {/* PIREP detail card — raw + plain text translation, reachable
          by tapping a PIREP marker. */}
      {pirep && (
        <PirepDetail
          pirep={pirep}
          onClose={() => setPirep(null)}
        />
      )}
      {/* Dip-site detail card — reachable by tapping a dip-site
          marker. Shows ownership, reimbursement, recent uses, and a
          re-lookup button. */}
      {dipSiteDetail && (
        <DipSiteDetail
          seed={dipSiteDetail}
          onClose={() => setDipSiteDetail(null)}
        />
      )}
      {/* ADS-B traffic detail card — reachable by tapping an aircraft icon. */}
      {trafficIcao && (
        <TrafficDetail
          icaoHex={trafficIcao}
          onClose={() => setTrafficIcao(null)}
        />
      )}
      {userWptDetail && (
        <UserWaypointDetail
          waypoint={userWptDetail}
          onClose={() => setUserWptDetail(null)}
        />
      )}

      {/* Fire Ops panel — bottom sheet toggled by the 🔥 FAB. */}
      {showFireOps && (
        <FireOpsPanel onClose={() => setShowFireOps(false)} />
      )}

      {/* Tail-number lookup sheet — bottom sheet listing recent flights. */}
      {tailLookupIdent && (
        <TailLookupSheet
          ident={tailLookupIdent}
          onClose={() => {
            setTailLookupIdent(null);
            setActiveFlightTrack(null);
          }}
          onShowTrack={(track) => setActiveFlightTrack(track)}
          activeTrackId={activeFlightTrack?.id ?? null}
        />
      )}

      {/* TFR tap popup — shown when user taps a fire-mission TFR polygon. */}
      {tfrPopup && (
        <div className="absolute z-40 bottom-24 left-3 right-3 max-w-sm mx-auto">
          <div className="bg-cockpit-900/97 backdrop-blur border border-red-700 rounded-xl p-3 shadow-2xl">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <div className="text-xs font-semibold text-red-400 tracking-wider">TFR — {tfrPopup.notamId}</div>
                {tfrPopup.tfrType && (
                  <div className="text-[10px] text-cockpit-300">{tfrPopup.tfrType}</div>
                )}
              </div>
              <button onClick={() => setTfrPopup(null)} className="text-cockpit-400 text-xs hover:text-cockpit-100">✕</button>
            </div>
            <div className="text-[11px] text-cockpit-200 mb-1">
              <span className="text-cockpit-400">Altitudes: </span>{tfrPopup.altitudeRange}
            </div>
            {tfrPopup.description && (
              <div className="text-[10px] text-cockpit-300 mb-2 leading-snug line-clamp-3">{tfrPopup.description}</div>
            )}
            <a
              href={tfrPopup.listUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-ember-400 hover:text-ember-300 underline"
            >
              View on FAA TFR site →
            </a>
          </div>
        </div>
      )}

      {/* Incident tap popup — shown when user taps a wildfire incident dot. */}
      {incidentPopup && (
        <div className="absolute z-40 bottom-24 left-3 right-3 max-w-sm mx-auto">
          <div className="bg-cockpit-900/97 backdrop-blur border border-orange-700 rounded-xl p-3 shadow-2xl">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <div className="text-xs font-semibold text-orange-400 tracking-wider">🔥 {incidentPopup.name}</div>
                <div className="text-[10px] text-cockpit-300">{incidentPopup.state}</div>
              </div>
              <button onClick={() => setIncidentPopup(null)} className="text-cockpit-400 text-xs hover:text-cockpit-100">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mb-2">
              <div>
                <span className="text-cockpit-400">Size: </span>
                <span className="text-cockpit-100">{incidentPopup.acreage.toLocaleString()} ac</span>
              </div>
              <div>
                <span className="text-cockpit-400">Contained: </span>
                <span className="text-cockpit-100">
                  {incidentPopup.containmentPct != null ? `${incidentPopup.containmentPct}%` : '—'}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-cockpit-400">Updated: </span>
                <span className="text-cockpit-100">{incidentPopup.lastUpdate}</span>
              </div>
            </div>
            {incidentPopup.inciwebUrl && (
              <a
                href={incidentPopup.inciwebUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-ember-400 hover:text-ember-300 underline"
              >
                View on InciWeb →
              </a>
            )}
          </div>
        </div>
      )}
    </div>
    </InstrumentContextProvider>
  );
}

// ---- airspace title/subtitle helpers used by the long-press picker ----
// Mirrors the same property-shape parsing we do in AirspaceDetail.tsx,
// but only enough to produce two short strings for the row.

/**
 * Emit a `flight.tfr_viewed` audit event when the pilot opens the
 * detail sheet for a TFR or SUA (the two airspace surfaces where
 * "saw it before flight" matters for audit). Other hazard kinds
 * (AIRMET / SIGMET / GFA / CWA) emit `flight.weather_viewed` since
 * they're weather phenomena, not airspace restrictions.
 */
function emitTfrViewedIfApplicable(kind: AirspaceKind, properties: Record<string, unknown>): void {
  const subjectIdRaw = properties.id ?? properties.notam_id ?? properties.name;
  const subjectId = subjectIdRaw != null ? String(subjectIdRaw) : undefined;
  if (kind === 'tfr' || kind === 'sua') {
    trackAuditEvent({
      kind: 'flight.tfr_viewed',
      subjectKind: kind,
      subjectId,
      payload: {
        airspaceKind: kind,
        title: airspaceTitle(kind, properties),
        subtitle: airspaceSubtitle(kind, properties),
      },
    });
  } else if (kind === 'airmet' || kind === 'sigmet' || kind === 'gfa' || kind === 'cwa') {
    trackAuditEvent({
      kind: 'flight.weather_viewed',
      subjectKind: kind,
      subjectId,
      payload: {
        hazardKind: kind,
        title: airspaceTitle(kind, properties),
        subtitle: airspaceSubtitle(kind, properties),
      },
    });
  }
}

function airspaceTitle(kind: AirspaceKind, p: Record<string, unknown>): string {
  const sx = (k: string) => (typeof p[k] === 'string' ? (p[k] as string).trim() : '');
  if (kind === 'sua')   return sx('NAME') || sx('IDENT') || sx('AREA_ID') || 'Special use airspace';
  if (kind === 'tfr')   return sx('TFR_SUBJECT') || sx('NAME') || sx('NOTAM_KEY') || 'TFR';
  if (kind === 'airmet')return `${sx('phenomenon') || sx('hazard') || 'AIRMET'}`;
  if (kind === 'sigmet')return `${sx('hazard') || sx('phenomenon') || 'SIGMET'}`;
  if (kind === 'gfa')   return gfaHazardLabel(sx('hazard')) || 'GFA Hazard';
  if (kind === 'cwa')   return gfaHazardLabel(sx('hazard')) || 'CWA';
  if (kind === 'dispatch') return sx('DispName') || sx('DispUnitID') || 'Dispatch zone';
  return 'Airspace';
}

function airspaceSubtitle(kind: AirspaceKind, p: Record<string, unknown>): string {
  const sx = (k: string) => (typeof p[k] === 'string' ? (p[k] as string).trim() : '');
  if (kind === 'sua') {
    const code = (sx('TYPE_CODE') || sx('TYPE')).toUpperCase();
    const labels: Record<string, string> = {
      P: 'Prohibited', R: 'Restricted', W: 'Warning',
      A: 'Alert', M: 'MOA', C: 'CFA', N: 'National security',
    };
    const upper = sx('UPPER_VAL') || sx('UPPER');
    const lower = sx('LOWER_VAL') || sx('LOWER');
    const limits = (lower || upper) ? ` · ${lower || 'SFC'} → ${upper || '—'}` : '';
    return (labels[code] || 'SUA') + limits;
  }
  if (kind === 'tfr') {
    const exp = sx('TFR_END') || sx('EFF_END') || sx('EXPIRES');
    return exp ? `Active TFR · ends ${exp}` : 'Active TFR';
  }
  if (kind === 'airmet') return sx('severity') ? `Severity: ${sx('severity')}` : 'G-AIRMET';
  if (kind === 'sigmet') return sx('severity') ? `Severity: ${sx('severity')}` : 'SIGMET';
  if (kind === 'gfa') {
    const sev = sx('severity');
    const fcst = sx('forecast');
    const valid = sx('validTime');
    const bits: string[] = [];
    if (sev) bits.push(`Severity: ${sev}`);
    if (fcst) bits.push(`+${fcst}h`);
    if (valid) bits.push(`valid ${valid.replace('T', ' ').replace(/:00\.000Z$/, 'Z')}`);
    return bits.length ? bits.join(' · ') : 'G-AIRMET hazard';
  }
  if (kind === 'cwa') {
    const cwsu = sx('cwsu');
    const exp = sx('validTimeTo');
    return [cwsu && `CWA · ${cwsu}`, exp && `valid until ${exp.replace('T',' ').replace(/:00\.000Z$/,'Z')}`].filter(Boolean).join(' · ') || 'Center Weather Advisory';
  }
  if (kind === 'dispatch') {
    const gacc = sx('GACCAbbreviation');
    const loc  = sx('DispLocation');
    const phone = sx('ContactPhone');
    const bits: string[] = [];
    if (gacc) bits.push(`${gacc} GACC`);
    if (loc)  bits.push(loc);
    if (phone) {
      const d = phone.replace(/\D/g, '');
      bits.push(d.length === 10 ? `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}` : phone);
    }
    return bits.join(' · ') || 'Fire dispatch zone';
  }
  return '';
}

/** Pretty label for a GFA hazard code. */
function gfaHazardLabel(code: string): string {
  switch (code.toUpperCase()) {
    case 'IFR':     return 'IFR / Low Ceilings';
    case 'MT_OBSC': return 'Mountain Obscuration';
    case 'ICE':     return 'Icing';
    case 'TURB-LO': return 'Turbulence (Low)';
    case 'TURB-HI': return 'Turbulence (High)';
    case 'LLWS':    return 'Low-Level Wind Shear';
    case 'FZLVL':   return 'Freezing Level';
    case 'SFC_WND': return 'Surface Winds ≥ 30 KT';
    default:        return code || 'GFA Hazard';
  }
}

// DataStrip is the configurable instrument bar at the bottom of the map.
// Tap it to open the InstrumentSelector and pick which 5 readouts to
// display from the catalog in src/lib/instruments.ts.
function DataStrip() {
  const ctx = useInstrumentContextValue();
  const selected = useInstrumentPrefsStore((s) => s.instruments);
  const highContrast = useInstrumentPrefsStore((s) => s.highContrastInstruments);
  const [pickerOpen, setPickerOpen] = useState(false);
  const airborneState = useAirborneStore((s) => s.state);
  // One-shot per-session override: if the user taps the badge, suppress
  // the badge (they've acknowledged / it's wrong and they don't want it
  // cluttering the strip). Resets on reload.
  const [badgeDismissed, setBadgeDismissed] = useState(false);
  const showBadge = !badgeDismissed && (airborneState === 'ground' || airborneState === 'airborne');

  // 1-Hz tick so readouts that depend on wall-clock time (Zulu, Flight
  // Time, ETA at *) refresh smoothly even when the GPS fix isn't
  // changing. Cheap because compute() functions are pure + sub-microsecond.
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNow((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <>
      <div data-fy-instrument-strip className="absolute left-3 right-3 bottom-3 z-10 pointer-events-none">
        <button
          onClick={() => setPickerOpen(true)}
          aria-label="Configure instrument bar"
          // Two looks:
          //  - Default (translucent ember-tinted) lets the map show
          //    through. Set 2026-05-09 per Wiley.
          //  - High-contrast (solid black + bold white) is for direct
          //    sunlight / polarized sunglasses. Toggle in
          //    Settings → Display. Wiley feedback 2026-05-12.
          className={[
            'mx-auto max-w-3xl w-full block backdrop-blur rounded-xl px-4 py-2 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 text-sm pointer-events-auto transition-colors border',
            highContrast
              ? 'bg-black/95 border-white/30 hover:bg-black'
              : 'bg-cockpit-800/55 hover:bg-cockpit-800/70 border-cockpit-600/70',
          ].join(' ')}
        >
          {selected.map((id) => {
            const def = INSTRUMENT_BY_ID[id];
            if (!def) return null;
            const v = def.compute(ctx);
            return <Stat key={id} label={def.shortLabel} value={v} highContrast={highContrast} />;
          })}
          {showBadge && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setBadgeDismissed(true); }}
              title={airborneState === 'ground'
                ? 'Ground mode — terrain alerts suppressed. Tap to dismiss.'
                : 'Airborne mode — terrain alerts active. Tap to dismiss.'}
              className={[
                'flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                airborneState === 'ground'
                  ? 'bg-cockpit-700/70 text-cockpit-300 hover:bg-cockpit-600/70'
                  : 'bg-ember-600/20 text-ember-300 hover:bg-ember-600/30',
              ].join(' ')}
              aria-label={airborneState === 'ground' ? 'Ground mode' : 'Airborne mode'}
            >
              {airborneState === 'ground'
                ? <Car size={12} aria-hidden />
                : <Plane size={12} aria-hidden />}
              <span>{airborneState === 'ground' ? 'GND' : 'AIR'}</span>
            </button>
          )}
        </button>
      </div>
      {pickerOpen && (
        <InstrumentSelector
          selected={selected}
          onChange={(next) => useInstrumentPrefsStore.getState().setInstruments(next)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </>
  );
}

function Stat({ label, value, highContrast }: { label: string; value: string; highContrast?: boolean }) {
  return (
    <div className="flex flex-col items-start">
      <span className={[
        'text-[10px] tracking-widest',
        highContrast ? 'text-white/80 font-semibold' : 'text-cockpit-300',
      ].join(' ')}>{label}</span>
      <span className={[
        'font-mono',
        highContrast ? 'text-white font-bold' : 'text-cockpit-100',
      ].join(' ')}>{value}</span>
    </div>
  );
}

/** Hidden component that wires alert triggers to GPS + plan + terrain
 *  state. Rendered once by MapPage so the alert watchers tear down
 *  automatically with the map view. */
function AlertTriggerWatcher() {
  const ctx = useInstrumentContextValue();
  useAlertTriggers({ terrainElevFt: ctx.terrainElevFt ?? null });
  return null;
}

/** Hidden host component that wires the AltitudeSlider's visibility
 *  to the set of altitude-aware layers currently active, and feeds it
 *  the live terrain-elev readout from the existing instrument context.
 *  Lives inside MapPage so the slider tears down with the map view. */
function AltitudeSliderHost() {
  const showTerrain = useLayerVisibilityStore((s) => s.layers.terrain);
  const showSmoke = useLayerVisibilityStore((s) => s.layers['hrrr-smoke']);
  const ctx = useInstrumentContextValue();
  // Add more layers to this OR-chain as future altitude-aware layers
  // come online (GFA icing/turb altitude band filter, freezing level
  // toggle, etc.). The slider stays mounted as long as any one of
  // them is active.
  // - terrain: TAWS color-relief ramp keyed by altitudeSliderFt
  // - hrrr-smoke (Phase 3): picks the closest published sigma level
  const visible = showTerrain || showSmoke;
  return (
    <AltitudeSlider
      visible={visible}
      terrainElevFt={ctx.terrainElevFt ?? null}
    />
  );
}
